import { useMemo } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { toRoomsByAvailability } from './pages/Rooms/useRooms/mappers';
import type { Room } from './pages/Rooms/useRooms/types';
import { useAvailableRooms, useStaticRooms } from './queries';
import { selectAlternativeRooms } from './selectAlternativeRooms';

const DATE_FORMAT = 'YYYY-MM-DD';

type Params = {
  journeyParams: ParsedJourneyParams;
  language?: string;
  currentRoomId: string;
  isUnavailable: boolean;
};

export function useAlternativeRooms({
  journeyParams,
  language,
  currentRoomId,
  isUnavailable,
}: Params): { alternatives: Room[]; isLoading: boolean } {
  const isFlagOn = useFeatureIsOn('feature.rooms.unavailable-alternatives');
  const enabled = isFlagOn && isUnavailable && !!currentRoomId;

  const { fromDate: jpFrom, toDate: jpTo, adults, children, childAges, rooms } = journeyParams;
  const fromDate = jpFrom.format(DATE_FORMAT);
  const toDate = jpTo?.format(DATE_FORMAT) || jpFrom.clone().add(1, 'days').format(DATE_FORMAT);

  const staticRoomsQuery = useStaticRooms(language, enabled);
  const availableRoomsQuery = useAvailableRooms(
    { fromDate, toDate, adults, children, childAges, rooms },
    enabled
  );

  const staticRooms = staticRoomsQuery.data?.data;
  const availableRooms = availableRoomsQuery.data?.data;
  const requestedQuantity = rooms?.length ?? 1;

  const alternatives = useMemo(() => {
    if (!enabled || !staticRooms || !availableRooms) {
      return [];
    }

    return selectAlternativeRooms(
      toRoomsByAvailability(staticRooms, availableRooms, requestedQuantity),
      currentRoomId
    );
  }, [enabled, staticRooms, availableRooms, requestedQuantity, currentRoomId]);

  return {
    alternatives,
    isLoading: enabled && (staticRoomsQuery.isFetching || availableRoomsQuery.isFetching),
  };
}
